import {
  decodeMapArtifact,
  verifyManifestHash,
  type MapManifest,
  type TerrainSampler,
  type TrackPhysicsConfig,
} from '@cars/shared';
import type { TrackConfig, Point2D } from '../types';
import { TerrainType } from '../types';

export class TrackRuntime {
  readonly drag: number;
  readonly torque: number;
  private sampler: TerrainSampler | null = null;
  private mapHash = '';

  readonly config: TrackConfig;

  constructor(config: TrackConfig) {
    this.config = config;
    this.drag = config.drag;
    this.torque = config.torque;
  }

  async initialize(
    bytes: ArrayBuffer | Uint8Array,
    manifest: MapManifest,
    source: { width: number; height: number },
  ): Promise<void> {
    const entry = manifest?.maps[String(this.config.id)];
    if (!bytes || !manifest || manifest.formatVersion !== 2 || !entry || entry.formatVersion !== 2)
      throw new Error(`Missing or unsupported compiled map for track ${this.config.id}`);
    const encoded = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
    await verifyManifestHash(encoded, entry.sha256);
    const decoded = decodeMapArtifact(encoded);
    if (
      decoded.width <= 0 ||
      decoded.height <= 0 ||
      decoded.width !== entry.width ||
      decoded.height !== entry.height ||
      decoded.width !== source.width ||
      decoded.height !== source.height
    ) {
      throw new Error(`Compiled map dimensions do not match track ${this.config.id}`);
    }
    this.sampler = decoded.sampler;
    this.mapHash = entry.sha256;
  }

  getMapHash(): string {
    return this.mapHash;
  }

  getSampler(): TerrainSampler {
    if (!this.sampler) throw new Error(`Track ${this.config.id} map is not ready`);
    return this.sampler;
  }

  getPhysicsConfig(): TrackPhysicsConfig {
    return this.config;
  }

  getTerrainAt(x: number, y: number): TerrainType {
    return this.getSampler().sample(x, y).terrain;
  }

  getCheckpointColorAt(x: number, y: number): number | null {
    return this.getSampler().sample(x, y).checkpointColor;
  }

  countTerrainPoints(points: Point2D[]): { gravelPoints: number; grassPoints: number } {
    let gravelPoints = 0;
    let grassPoints = 0;

    for (const point of points) {
      const terrain = this.getTerrainAt(point.x, point.y);
      if (terrain === TerrainType.Gravel) {
        gravelPoints += 1;
      }
      if (terrain === TerrainType.Grass) {
        grassPoints += 1;
      }
    }

    return { gravelPoints, grassPoints };
  }
}
